import { useQuery } from '@tanstack/react-query';
import { ListChecks, SearchX } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { groupsOf, type AccessGroup } from '@/api/auth';
import { rolesQuery, type Role } from '@/api/vacancies';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { mayReadVacancyCatalogue } from '@/pages/Candidates/candidateAccess';
import { candidatesUrl } from '@/pages/Candidates/candidateFilters';
import NotFound from '@/pages/NotFound';
import { type } from '@/theme/typography';

import { NewVacancyButton } from './NewVacancyButton';
import { VacancyDialog } from './VacancyDialog';
import { VacancyMenu } from './VacancyMenu';
import { isImportedVacancy, requisitionStatusRendering } from './requisition';
import {
  couldBeHidingVacancies,
  readVacancyFilters,
  toRoleListQuery,
  vacancySearch,
  VACANCY_PAGE_SIZE,
} from './vacancyUrls';

/**
 * `DESIGN.md` §6.8 — the vacancy catalogue, **read-only** (TASK-071; the writes are TASK-072's).
 *
 * ⚠️ **A hairline-ruled grid, not a `Table`** — §6.8 says so in as many words (*"a hairline-ruled grid,
 * not a table — few vacancies, each carrying counts worth seeing"*), and the reason it is not a
 * preference is that each entry here is a small block of five different things rather than a row of
 * comparable columns. Below `lg` it is one column (§10). The hairlines are the `gap-px` over `bg-rule`
 * under cells on `bg-paper`, so no cell carries a border of its own and no two borders ever meet.
 *
 * ⚠️ **Admin and Recruiter, and the evidence is server-side.** `GET /api/v1/roles/` answers an
 * **Interviewer 403** — `Route("get", ROLES, None, (401, 403, 200, 200, 403, 403))` in
 * `backend/tests/test_permission_matrix.py`, which is the route's own Sprint 2 group gate
 * (`read_access = require_group(GROUP_ADMIN, GROUP_RECRUITER)`, `b1e125d`). It is **not** TASK-029's
 * candidate-surface 404 rule, a mis-citation this sprint's board records and corrects. The route is
 * mounted behind `RequireGroup` with the groups §5 gives it (`routes.tsx`), which decides what is
 * *rendered* and never what may be *read*; the client gate is never the evidence.
 *
 * ⚠️ **The question count is the *active* count, and there is no total to show.** `RoleOut` carries
 * `active_question_count` and no total, so ADR 0005's mockup phrasing (*"7 active of 7 questions"*) is
 * **unrenderable here** — this grid labels the figure *Active questions* rather than implying a
 * denominator it does not have. Inventing a field or making a second request to fake the total is the
 * one thing that would make this cell disagree with the server.
 *
 * ⚠️ **Two claims this paragraph carried are retracted, and they outlived ADR 0018 by a sprint**
 * _(corrected 2026-08-12 with TASK-144)_. It said §6.9 *"holds the whole bank in one request and does
 * say '7 active of 9 questions'"* for **this vacancy**, and that the figure here is *"a link into that
 * bank for an Admin and plain text for a Recruiter"*. Neither is true: ADR 0018 made §6.9 the tenant's
 * **library**, which says nothing about any vacancy, and deleted the address the link pointed at — so
 * there was no second figure to be read against and no destination to link to. The figure is plain text
 * for **every** group, which §6.8 re-decided rather than inherited when a per-vacancy view of sets came
 * back (the row menu's *Question sets*, below): the reasons are in `DESIGN.md` §6.8. ⚠️ The
 * retracted-claims registry did **not** catch this one — its forms are anchored to *"its own bank"* and
 * this site wrote *"that bank"* — which is a gap worth reporting rather than papering over.
 *
 * ⚠️ **A vacancy imported from ADP is read-only here, and the cell says where it is maintained**
 * (Sprint 24, TASK-178 — ADR 0020 decision 1). **Five** keys carry it: `external_source` (empty on
 * every vacancy Panel's own screens created, which is what makes *"absent means ours"* the whole
 * rule), `requisition_number`, `requisition_status`, `locations` and — since 2026-11-03, ADR 0020
 * decision 9 — `hiring_manager`. ⚠️ **That count read *"Four keys"* until the fifth arrived**, which
 * is the same correction the backend made to `RoleOut`'s own docstring in the same sprint. Three
 * things follow on this screen and
 * each has its own note below — the state word comes from the **status** and never from `is_open` as
 * well (`VacancyState`), the provenance and the sentence standing where *Edit* is not (`Provenance`),
 * and the row menu keeping *Question sets* alone (`VacancyMenu`). ⚠️ **None of it is the enforcement:**
 * `PATCH /api/v1/roles/{role_id}/` answers **409** for every caller on such a row, an `Admin`
 * included, and that refusal is the backend's (TASK-175).
 *
 * ⚠️ **The writes are Admin's, and there is no delete among them** (TASK-072, ADR 0005 — **feedback
 * protection #2**). *New vacancy* opens `VacancyDialog`, each cell carries `VacancyMenu` with *Edit*,
 * *Question sets* (Sprint 20 — which sets this vacancy **offers**, ADR 0018 decision 3) and *Close* /
 * *Reopen*, and closing is `PATCH {"is_open": false}` — after which the cell is still here, carrying the
 * word *Closed*. Nothing on this screen matches `/delete|remove/i`, in any state and
 * with any dialog open, and `vacancyWrites.test.tsx` is what keeps it that way.
 */
export default function Vacancies() {
  const { t } = useTranslation('vacancies');
  const session = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * §6.8's create dialog, mounted **once** by the screen and only while open, so both places the
   * button appears open the same form rather than each holding one of its own — §6.3's seam, and the
   * reason `NewVacancyButton` takes an `onClick` at all.
   */
  const [creating, setCreating] = useState(false);

  /**
   * The address bar is the filter (`vacancyUrls.ts`), so a page is linkable, the back button walks
   * back through pages, and §6.2's setup path can link in (TASK-077).
   */
  const filters = readVacancyFilters(searchParams);

  // The group decides what may be read here as well as what is rendered, so it is read before the
  // query rather than after it. The empty list errs towards rendering no action, never towards
  // rendering one a group may not use.
  const groups: AccessGroup[] = session.data ? groupsOf(session.data) : [];

  /** Whether this viewer may read the catalogue at all: Admin or Recruiter (`candidateAccess.ts`). */
  const mayRead = mayReadVacancyCatalogue(groups);

  /**
   * ⚠️ **Page 1 asks with no query at all**, which is what shares the entry §6.3's `VacancyFilter` and
   * §6.4's dialog already hold rather than fetching the identical body under a second key
   * (`toRoleListQuery`, `VACANCY_PAGE_SIZE`). Since TASK-070 the key carries the query, so page 2 is
   * its own entry and cannot be served to either of them.
   *
   * **No `placeholderData`, no `keepPreviousData`, no `select`.** Sprint 8 shipped `keepPreviousData`
   * on a correctly keyed query and it resolved from the observer's last payload under *any* key while
   * reporting success — a paginated grid is exactly where that looks like a feature.
   */
  const catalogue = useQuery({ ...rolesQuery(toRoleListQuery(filters)), enabled: mayRead });

  // A page is a new address, not a correction of the previous one: Back goes back a page.
  const goToPage = (page: number) => setSearchParams(vacancySearch({ page }), { replace: false });

  /**
   * ⚠️ **While the session is resolving the group is unknown**, and the honest answer then is §8's
   * loading state rather than a refusal — which is what this screen renders anyway until the catalogue
   * lands (`Chooser`'s and §6.9's shape, for the same reason). `RequireSession` is upstream of the
   * route, so this is a frame rather than a state anyone sits in.
   */
  if (!session.data) {
    return (
      <>
        <ScreenTitle />
        <LoadingCells />
      </>
    );
  }

  /**
   * ⚠️ **The read gate, and it is a refusal rather than a skipped request** — a **sprint-end review
   * finding** (`security-auditor`, 2026-07-29). This screen was the one reader of the viewer-less
   * `roles` key with no gate of its own: it had no `enabled`, no predicate, and it read
   * `catalogue.data.items` unconditionally, so it depended entirely on `RequireGroup` in `routes.tsx`
   * having remembered. Mounted outside that route with an Interviewer session and the `['roles', null]`
   * entry a Recruiter fills legitimately on §6.3 or §6.4, the whole catalogue rendered **with no
   * request at all** — Sprint 9's `VacancyFilter` defect exactly, on a screen where every existing
   * assertion counts requests and so stayed green.
   *
   * `enabled: false` stops the request; it does not stop this observer resolving from an entry already
   * at the key, and the key carries no viewer on purpose (`queryKeys.roles`: a demotion without a
   * re-login keeps the user id, so a viewer segment could not close this). So the refusal is here,
   * before a line below reads what the query holds, and it is the answer `RequireGroup` gives at the
   * route — byte-identical to an address that does not exist, because a Forbidden state would tell an
   * Interviewer that a screen §5 refuses them is there.
   *
   * It is deliberately **not** redundant with the route's gate: this component decides what it renders
   * from what it holds, whatever mounts it, and *"a guard that depends on the route table having
   * remembered is the one Sprint 9 shipped"* (`QuestionBank/Chooser.tsx`). The predicate is
   * `mayReadVacancyCatalogue` and not one of its own, because the question is the same request's
   * question — §6.3's filter, §6.4's dialog and this grid must not hold two answers to it.
   */
  if (!mayRead) return <NotFound />;

  if (catalogue.isError) {
    /**
     * One mapping for every failure (`ApiErrorState`): offline, forbidden, throttled, or this screen's
     * own sentence.
     *
     * ⚠️ **A 404 here stays §8's Error state.** `/api/v1/roles/` is a **collection** route every
     * caller who is answered at all may ask for, so a 404 from it is a broken deployment and reads
     * like one; `isOutsideReach` is false for a vacancy route on purpose (`api/vacancies.ts`), and
     * nothing was added to `CANDIDATE_SCOPED_RECORD` — *"You do not have access to this candidate."*
     * over a mistyped catalogue address would be a sentence about a candidate nobody named. The
     * *record*-route 404 (`/vacancies/9999/questions`) is answered at §6.9's screen, TASK-073's.
     */
    return (
      <>
        <ScreenTitle />
        <div className="mt-10">
          <ApiErrorState
            error={catalogue.error}
            message={t('error')}
            onRetry={() => void catalogue.refetch()}
          />
        </div>
      </>
    );
  }

  if (catalogue.isPending) {
    return (
      <>
        <ScreenTitle />
        <LoadingCells />
      </>
    );
  }

  const rows = catalogue.data.items;
  const count = catalogue.data.count;
  const first = rows.length === 0 ? 0 : (filters.page - 1) * VACANCY_PAGE_SIZE + 1;
  const last = rows.length === 0 ? 0 : first + rows.length - 1;

  return (
    <>
      {/*
       * ⚠️ **The header action is offered only when the grid has rows, and that is not a layout
       * preference.** §8's two empty states already decide the question: *"No vacancies yet."* carries
       * **New vacancy** as its one action, and *"No vacancies on this page."* — a `?page=` past the end
       * — deliberately carries **none**, because *"offering the create action to somebody whose
       * bookmarked address merely overshot is how a second vacancy for the same job gets made"*
       * (§6.8, TASK-071). A header button rendered in every state would put it back on exactly that
       * address. So there is **one** *New vacancy* control on screen at any moment: here when there is
       * a catalogue, in the empty state when there is not.
       */}
      <ScreenTitle
        action={
          rows.length > 0 ? (
            <NewVacancyButton groups={groups} onClick={() => setCreating(true)} />
          ) : undefined
        }
      />

      {rows.length === 0 ? (
        /**
         * §8's two empty states, which are two states and not one sentence with a variable in it.
         * *"No vacancies yet."* is an empty tenant and its action is the one thing that helps — **New
         * vacancy**, and only for a viewer who may (`NewVacancyButton`). *"No vacancies on this
         * page."* is an address past the last page, and its action is the way back to the first one.
         */
        <div className="mt-6">
          {couldBeHidingVacancies(filters) ? (
            <EmptyState
              icon={SearchX}
              message={t('empty.page')}
              action={
                <Button variant="secondary" onClick={() => goToPage(1)}>
                  {t('empty.firstPage')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={ListChecks}
              message={t('empty.none')}
              action={<NewVacancyButton groups={groups} onClick={() => setCreating(true)} />}
            />
          )}
        </div>
      ) : (
        <ul aria-label={t('grid.label')} className={cn(GRID, 'mt-6')}>
          {/*
           * ⚠️ **Rendered in the payload's order, and nothing here re-sorts it.** The API orders
           * `-is_open, title, id` (`Role.Meta.ordering`), so **closed vacancies come last and are
           * never hidden** — §6.8: *"Never hidden — feedback attached to them still matters."* A
           * client-side sort would only order the rows of the page it holds, which on page 2 of a
           * paginated catalogue is a different order from the one the reader was promised.
           */}
          {rows.map((role) => (
            <VacancyCell
              key={role.id}
              role={role}
              groups={groups}
              viewerId={session.data.user.id}
            />
          ))}
          {/*
           * ⚠️ **The tracks no vacancy fills, filled** — a `ui-reviewer` **live-pass finding**
           * (2026-07-29, measured in a real browser on the dev tenant). `bg-rule` under `gap-px` **is**
           * the hairline mechanism, so it needs the cells to tile the grid completely; with a row count
           * that is not a multiple of `GRID_COLUMNS` they do not, and what showed through at four items
           * was **758 × 144px of `rgb(229, 226, 220)`** with `elementFromPoint` returning the `<ul>`
           * itself — read as an empty card rather than as space. It disappeared only when
           * `count % 3 === 0`, which is why the three-vacancy fixture looked right while the real dev
           * tenant, with two, did not.
           *
           * ⚠️ **Why this holds at every item count and not just at four.** The cells rendered are
           * always `rows.length + fillerCells(rows.length)`, which is a multiple of `GRID_COLUMNS` for
           * **any** count ≥ 1 — 1→3, 2→3, 4→6, 5→6, 7→9, and 3→3 and 6→6 with no filler at all — so the
           * last row is never partly empty. Below `lg` there is one column, where every count tiles
           * completely on its own, and these are `hidden` there rather than being three empty rows of
           * their own. At **zero** rows the grid is not rendered at all (§8's two empty states replace
           * it) and the loading state's cells are a fixed six (`LoadingCells`).
           *
           * They are `aria-hidden` and carry nothing: a screen reader is told the list has as many
           * items as the tenant has vacancies, which `vacancyGrid.test.tsx` asserts as a count of
           * `listitem` roles beside the count of DOM children.
           */}
          {Array.from({ length: fillerCells(rows.length) }, (_, index) => (
            <li key={`filler-${index}`} aria-hidden="true" className="hidden bg-paper lg:block" />
          ))}
        </ul>
      )}

      {/*
       * §6.8 says nothing about pagination and the route truncates at 50, so a catalogue that is cut
       * off says so — a grid that ends at cell 50 with no total reads as the whole catalogue.
       * `DESIGN.md` §6.8 is extended with this in the same pass.
       */}
      {count > 0 && (
        <nav
          className="mt-4 flex flex-wrap items-center justify-between gap-3"
          aria-label={t('pagination.label')}
        >
          <p className={type.metadata}>{t('pagination.range', { first, last, count })}</p>
          <span className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => goToPage(filters.page - 1)}
            >
              {t('pagination.previous')}
            </Button>
            {/*
             * ⚠️ `rows.length === 0` is not redundant with `last >= count` — §6.3's own sprint-end
             * finding, inherited here on purpose. On an empty page `first` and `last` are both `0`, so
             * `0 >= 137` is false and *Next* would stay live on `?page=99`, walking to page 100 and
             * every page after it. An empty page has no next page whatever the total says.
             */}
            <Button
              variant="secondary"
              size="sm"
              disabled={rows.length === 0 || last >= count}
              onClick={() => goToPage(filters.page + 1)}
            >
              {t('pagination.next')}
            </Button>
          </span>
        </nav>
      )}

      {/*
       * Mounted only while open, so the form's defaults are fresh every time and a cancelled draft
       * cannot come back on the next press (`VacancyDialog`, `EditPersonDialog`'s precedent).
       */}
      {creating && <VacancyDialog onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * The hairline-ruled grid itself (§6.8), and one column below `lg` (§10). `gap-px` over `bg-rule` with
 * cells on `bg-paper` is what makes every rule a single hairline: cells carry no borders, so two of
 * them never sit side by side and no cell needs to know which edge of the grid it is on.
 *
 * ⚠️ **The mechanism's own condition: the cells must tile the grid.** A coloured background showing
 * through `gap-px` is a hairline only where two cells meet — anywhere else it is a coloured rectangle,
 * which is the live-pass defect `fillerCells` answers at the call site. `GRID_COLUMNS` is the count
 * written into `lg:grid-cols-3` above, and `vacancyGrid.test.tsx` asserts the two agree, because a
 * second column count added here without one there would put the grey back.
 */
const GRID =
  'grid grid-cols-1 gap-px overflow-hidden rounded border border-rule bg-rule lg:grid-cols-3';

/** The columns the grid has at `lg` — `GRID`'s own `lg:grid-cols-3`, asserted equal to it. */
const GRID_COLUMNS = 3;

/**
 * How many inert cells the last row needs so that no track of the coloured background is left bare.
 * Zero when the count already fills its rows, and zero for an empty grid, which is never rendered.
 */
function fillerCells(cells: number) {
  return cells === 0 ? 0 : (GRID_COLUMNS - (cells % GRID_COLUMNS)) % GRID_COLUMNS;
}

/**
 * §9: one `h1`, and it is the screen's own — in every one of §8's five states.
 *
 * The action beside it is optional and absent in four of the five: an error, a loading grid and an
 * overshot page are none of them a moment to offer **New vacancy** (see the call site).
 */
function ScreenTitle({ action }: { action?: ReactNode }) {
  const { t } = useTranslation('vacancies');

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h1 className={type.pageTitle}>{t('title')}</h1>
      {action}
    </div>
  );
}

/** §8's loading state: cells shaped like cells, never a centred spinner. */
function LoadingCells() {
  const { t } = useTranslation('vacancies');

  return (
    <div className={cn(GRID, 'mt-6')} aria-busy="true">
      <span className="sr-only">{t('loading')}</span>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="flex flex-col gap-4 bg-paper p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * One vacancy: the abbreviation avatar, the title, the department, the open/closed state **as a word**,
 * and the two counts each labelled for what it is.
 */
function VacancyCell({
  role,
  groups,
  viewerId,
}: {
  role: Role;
  groups: AccessGroup[];
  /** The session user's id — the row menu's *Question sets* reads a list narrowed per caller. */
  viewerId: number;
}) {
  const { t } = useTranslation('vacancies');

  return (
    <li className="flex flex-col gap-4 bg-paper p-4">
      <div className="flex items-start gap-3">
        {/*
         * §4: for a vacancy the fallback is its abbreviation, not initials of the title.
         *
         * ⚠️ **This is the only thing on a closed cell that is dimmed, and §6.8's "reduced opacity"
         * was narrowed to it deliberately** — see the note added to §6.8 in this pass. `opacity` on
         * the whole cell takes every `ink-500` label inside it to 4.28:1 at 70% (composited over
         * paper), and §9's 4.5:1 floor is not negotiable; the avatar is decorative, which is the one
         * place §3 allows a mark to fall below it. The **word** is what carries the state.
         */}
        <Avatar
          name={role.title}
          abbreviation={role.abbreviation}
          className={cn(!role.is_open && 'opacity-60')}
        />
        <div className="min-w-0 flex-1">
          <h2 className={cn(type.bodyPrimary, 'font-medium [overflow-wrap:anywhere]')}>
            {role.title}
          </h2>
          <p className={cn(type.metadata, '[overflow-wrap:anywhere]')}>{role.department}</p>
          <Provenance role={role} />
        </div>
        {/*
         * ⚠️ **The state carries its text** (§3, §9), which is the correction this pass makes to
         * §6.8: a closed vacancy marked by opacity alone is invisible to a screen reader and to
         * anybody who has no open cell beside it to compare against. Neutral ink for both words —
         * open and closed are a *state*, not a judgement, and "a five-colour status palette is how an
         * interface starts shouting" (`statusDotStyles`).
         */}
        <VacancyState role={role} />

        {/*
         * §6.8's row actions — *Edit* and *Close* / *Reopen*, **and no delete** (ADR 0005). Renders
         * nothing at all for a Recruiter, who reads this screen and writes nothing on it
         * (`VacancyMenu`, `vacancyAccess.ts`).
         */}
        <VacancyMenu role={role} groups={groups} viewerId={viewerId} />
      </div>

      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        <div>
          {/*
           * *Active* questions, and the label says so: the payload has no total (see this module's
           * header).
           *
           * ⚠️ **Plain text for every group since Sprint 19, and the figure itself is unchanged**
           * (2026-08-11, ADR 0018). It was a **link into §6.9 for an Admin and plain text for a
           * Recruiter** — the asymmetry of `Route("get", BANK, …, (401, 403, 403, 200, 403, 403))`,
           * which answered a Recruiter 403 on the bank while answering them 200 on this catalogue. The
           * address it pointed at (`/vacancies/:vacancyId/questions`) no longer exists and **no
           * per-vacancy question screen replaces it in this sprint**, so there is nowhere for the
           * figure to lead: an `href` to an unmounted route is what `personCard.test.tsx`'s dead-link
           * guard exists to catch, and linking every reader to the tenant-wide library instead would
           * have been a link that silently drops the vacancy it was pressed from. ⚠️ **That
           * alternative's noun is retracted** (2026-09-24, ADR 0019): there is no such destination to
           * have refused, and `/questions` lists the tenant's sets.
           *
           * ⚠️ **`active_question_count` still means what it meant** — the vacancy's active questions,
           * counted now over the sets attached to it — so the number is the same number and keeps its
           * label.
           *
           * ⚠️ **The last sentence read *"Attaching sets to a vacancy is Sprint 20's, and the screen
           * that figure could point at again arrives with it"* until 2026-08-12.** Sprint 20 shipped the
           * attaching — it is this row menu's *Question sets* item (`VacancySetsDialog`, TASK-144) — and
           * the figure stays **plain text for every group**, re-decided rather than inherited: the
           * dialog sits behind an `Admin`-only menu item, so a figure that opened it would work for one
           * group and do nothing for the other, and there is still no per-vacancy question **address**
           * to link to. §6.8 carries the whole re-decision.
           */}
          <dt className={type.eyebrow}>{t('counts.questions')}</dt>
          <dd className={cn(type.numeral, 'text-sm text-ink-900')}>{role.active_question_count}</dd>
        </div>
        <div>
          <dt className={type.eyebrow}>{t('counts.candidates')}</dt>
          <dd className={cn(type.numeral, 'text-sm')}>
            {/*
             * §6.8's *active candidates* is `NEW`/`INTERVIEWING`/`OFFER`, which is **exactly** §6.3's
             * active-only default — the absence of `status` in the address (`candidateFilters.ts`).
             * So the link names only the vacancy and lets that default do the rest, built through
             * `candidatesUrl()` and never spelled out: a second place that writes `?vacancy=` is a
             * second place that can write it wrong, and a filter the API ignores answers with *every*
             * row while the chip claims a filter is applied.
             */}
            <Link
              to={candidatesUrl({ vacancy: role.id })}
              aria-label={t('counts.candidatesLink', {
                title: role.title,
                count: role.active_candidate_count,
              })}
              className="rounded text-ink-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {role.active_candidate_count}
            </Link>
          </dd>
        </div>

        {/*
         * **Whose requisition this is** (2026-11-03, ADR 0020 decision 9, TASK-186). ADP sends a
         * `hiringManager` on 94 of the 95 requisitions measured on the wire, and the name read off
         * it is `personName.formattedName` — pinned on the backend rather than guessed, because
         * BUG-114 was caused by reading a name out of a nesting ADP does not send.
         *
         * ⚠️ **An absence is an absence, and that is the whole treatment** — no label, no dash, no
         * *"Unknown"*, no empty row. Three different rows meet this branch and the cell is identical
         * on all three: a vacancy Panel's own screens created (`hiring_manager` is `""`, as every
         * provenance key on such a row is), the **one** imported requisition of the 95 that carries
         * no manager, and any future row where ADP stops sending one. It is `Locations`' rule one
         * cell over, for `Locations`' reason: an empty labelled cell would be a claim about ADP's
         * data rather than about this payload. Nothing here is ADP-specific either — the condition
         * is the **value** and never `isImportedVacancy`.
         *
         * ⚠️ **This is a customer employee's name, not a candidate's**, so it is personal data that
         * a candidate's erasure does not touch (ADR 0009 clears the *candidate*'s fields) and that
         * must reach no log line and no error message. It is rendered and nothing else is done with
         * it — there is no `console`, no analytics and no id behind it to resolve, because the
         * hiring manager has no account in this tenant.
         */}
        {role.hiring_manager !== '' && (
          <div>
            <dt className={type.eyebrow}>{t('requisition.hiringManager')}</dt>
            {/* `[overflow-wrap:anywhere]` for the reason every other free-text cell here carries it
                (TASK-069): a long single-token name must not set the grid's min-content width. */}
            <dd className={cn(type.body, 'text-sm [overflow-wrap:anywhere]')}>
              {role.hiring_manager}
            </dd>
          </div>
        )}

        {/*
         * ⚠️ **A vacancy may be open in more than one place, and the measured case is two** — one ADP
         * requisition carried Vilnius *and* Warsaw (2026-10-28). So this is a **list** and not a
         * string somebody has to parse, rendered in the payload's own order, which is ADP's array
         * order carried on each row's `position` (ADR 0020 decision 5b).
         *
         * **One location renders as one name and none renders nothing at all** — an empty labelled
         * cell would say the vacancy has no place, which is a claim about ADP's data rather than
         * about this payload. Nothing here is ADP-specific: the model lets a hand-created vacancy
         * carry locations too, and this cell renders whatever arrives, which is why the condition is
         * the list's length and not the source.
         */}
        {role.locations.length > 0 && (
          <div>
            <dt className={type.eyebrow}>{t('requisition.locations')}</dt>
            <dd className={cn(type.body, 'text-sm [overflow-wrap:anywhere]')}>
              {role.locations.map((location) => location.name).join(' · ')}
            </dd>
          </div>
        )}
      </dl>
    </li>
  );
}

/**
 * The vacancy's state **as one word**, read off one column.
 *
 * ⚠️ **An imported vacancy shows its requisition status and a hand-created one shows Open/Closed, and
 * no cell ever shows both** (ADR 0020 decision 5a, lesson 3). `Role.is_open` is a *projection* of
 * `requisition_status` on an ADP-sourced row — written by the one statement that writes the status,
 * never an input — so rendering both would put two markers of one fact in front of one reader, which
 * is the hazard `CONTEXT.md` states about `submitted_at` (*"there is no `is_draft`, no `state` and no
 * second flag"*). On every vacancy Panel owns, the boolean keeps the two-word rendering it has had
 * since TASK-071 and nothing about this cell moves.
 *
 * ⚠️ **On hold must never read as Closed**, which is the whole reason the boolean could not carry
 * this: a recruiter told *"closed"* about a paused requisition has been told something false, and the
 * same is true of a cancelled one — an abandoned search is not a finished one.
 */
function VacancyState({ role }: { role: Role }) {
  const { t } = useTranslation('vacancies');

  const requisition = isImportedVacancy(role)
    ? requisitionStatusRendering(role.requisition_status)
    : null;

  /*
   * A source with no status, or a status outside the five, renders the plain Open/Closed word rather
   * than nothing: the importer admits neither state (ADR 0020 decision 5a — an unknown status imports
   * nothing and is reported by `itemID`), so this is a cell with no reader rather than a case to
   * design, and an empty corner would read as a missing value.
   */
  const label = requisition
    ? t(requisition.labelKey)
    : t(role.is_open ? 'state.open' : 'state.closed');
  const isOpen = requisition ? requisition.isOpen : role.is_open;

  return (
    <span
      className={cn(
        type.eyebrow,
        'shrink-0 rounded px-1.5 py-0.5',
        isOpen ? 'text-ink-500' : 'border border-rule-strong text-ink-700',
      )}
    >
      {label}
    </span>
  );
}

/**
 * **Where this vacancy came from, and why it has no Edit** — decision 1's visible half (TASK-178).
 *
 * Two sentences and they answer two different questions. The first names the **source** and the
 * **requisition number** — `clientRequisitionID`, `1096`, the number a recruiter says out loud, and
 * never `itemID`, which is a key no screen has a use for. The second says the record is maintained in
 * ADP, which is what the missing Edit means.
 *
 * ⚠️ **This is ADR 0008's shape rather than §5's, and the difference is worth stating because the two
 * look identical from a distance.** §5 hides what a **group** may not do: rendering it would tell this
 * reader a capability exists and is being withheld *from them*, which is true, so the control goes and
 * nothing is said. Here the refusal is a property of the **record** — no Panel user may edit an
 * imported requisition, an `Admin` included, which is why the server answers **409** and not 403 —
 * so an absence alone would leave the one reader who edits every *other* vacancy to work out why this
 * one has nothing. That is the task's own sentence: *a field that cannot be edited and does not say
 * why is a bug report*. So the control is **absent** (never disabled: a disabled control says *"you
 * may do this, but not now"*, which is false in both halves) **and a sentence stands where it is
 * not** — exactly what ADR 0008 does where §6.7 has no edit button.
 *
 * It renders for **every** reader, including a Recruiter who is offered no Edit on any vacancy: the
 * provenance is a fact about the record rather than an explanation addressed to one group, and the
 * requisition number is the thing somebody quotes when they go and change it where it is changed.
 */
function Provenance({ role }: { role: Role }) {
  const { t } = useTranslation('vacancies');

  if (!isImportedVacancy(role)) return null;

  return (
    <>
      <p className={cn(type.metadata, '[overflow-wrap:anywhere]')}>
        {role.requisition_number === ''
          ? t('requisition.from', { source: role.external_source })
          : t('requisition.fromNumbered', {
              source: role.external_source,
              number: role.requisition_number,
            })}
      </p>
      {/*
       * Information rather than a fault — no `role="alert"` and no `negative` ink. Nothing has gone
       * wrong here: the record simply has another owner, and §6.4's empty-vacancy notice is the shape
       * this product already uses for that.
       */}
      <p className={cn(type.metadata, '[overflow-wrap:anywhere]')}>
        {t('requisition.maintained', { source: role.external_source })}
      </p>
    </>
  );
}
